import { adapterPlanogramItemToPlanogramItemCommon } from "@CommonAdapters/pog";
import {
  PlanogramContainer,
  PlanogramContainerCommonNonCircular,
  UnrangedPlanogramContainer,
} from "./container";
import {
  PlanogramItemCommonNonCircular,
  PlanogramItemNonCircular,
  UnrangedPlanogramItem,
  UnrangedPlanogramItemNonCircular,
} from "./item";
import { Planogram, PlanogramNonCircular } from "./planogram";
import { PlanogramBay, PlanogramBayNonCircular } from "./bay";
import { PlanogramShelfNonCircular } from "./shelf";

export const latestPogVersion = "3.9";
/**
 * 3.9 - Add effectiveWeightLimit to shelves (default null): the cap the last
 *       pogger run judged the shelf against, with its provenance. The ONLY
 *       source of a shelf limit — null means no verdict, never a default.
 * 3.8 - Add weight and weightUom to items (both default null). A STATED weight
 *       from a data source, never the derived one — see productWeight.ts.
 * 3.7 - Add unitsDeep to items and weightCapacity to shelves (both default null)
 * 3.6 - Add item capping properties (xCapNum/yCapNum/zCapNum and orientations).
 *       Shipped without a changelog line or a latestPogVersion bump; recorded
 *       here after the fact — the adapter block always existed.
 * 3.5 - PlanogramPegboardNonCircular - rename items to placements. Add pegProduct.
 * 3.4 - Add maxShelfHeight and maxProductHead to bays
 * 3.3 - Remove coordinates from bays and use width and height instead
 * 3.2 - Separate item squeezeDimension object
 * 3.1 - Add product ranking
 * 3.0 - PlanogramContainer label replaced with cdts, old data can't be upgraded correctly 100%
 * 2.15 - spaceMix in pogItem
 * 2.14 - squeeze dimensions support
 * 2.13 - unranged containers support
 * 2.12 - containers have sales information
 * 2.11 - add variantRestrictions to shelves
 * 2.10 - shelves ordered from top to bottom
 * 2.9 - add relaxedConstraints to Pog
 * 2.8 - transform from unranged item reason to reasons
 * 2.7 - removed hardcoded shelfNo and bayNo
 * 2.6 - unranged items have potential shelves
 * 2.5 - containers inside pog
 * 2.4 - dividerWidth support
 * 2.3 - grillHeight support
 * 2.2 - initial hangcells support
 * 2.1 - first pog with provided version, profit added to PlanogramItem, version added to Pog
 */

type PlanogramItemShelfRelaxtion =
  | "min_width"
  | "cdt0_contiguous"
  | "cdt1_contiguous"
  | "cdt2_contiguous"
  | "cdt3_contiguous"
  | "cdt1_order"
  | "cdt2_order"
  | "cdt3_order";
type PlanogramItemRelaxation =
  | "facings_not_enough"
  | "facings_too_many"
  | "units_not_enough_mos"
  | "units_not_enough_dos"
  | "units_too_many";

export type RelaxedItemConstraint = {
  relaxationType: PlanogramItemShelfRelaxtion | PlanogramItemRelaxation;
  entity: { itemId: string; bayNo: number; shelfNo: number };
  magnitude: number | "fully_relaxed";
};

export type RelaxedShelfConstraint = {
  relaxationType: PlanogramItemShelfRelaxtion;
  entity: [number, number];
  magnitude: number | "fully_relaxed";
};

export type PlanogramRelaxConstraint =
  | RelaxedItemConstraint
  | RelaxedShelfConstraint
  | {
      relaxationType: string;
      entity: string;
      magnitude: string;
    };

interface PogCommon {
  version: string;
  relaxedConstraints: PlanogramRelaxConstraint[];
  metrics: {
    returnOnSpace: number | null;
    shelfAlignment: number | null;
    coreRange: number | null;
    dosMos: number | null;
  };
}

export interface PogNonCircular extends PogCommon {
  containers: PlanogramContainerCommonNonCircular[];
  unrangedContainers: PlanogramContainerCommonNonCircular[];
  planogram: PlanogramNonCircular;
  unrangedItems: UnrangedPlanogramItemNonCircular[];
  deletedItems: (PlanogramItemCommonNonCircular & { storeCodes: string[] })[];
}

export class Pog implements PogCommon {
  version: string;
  relaxedConstraints: PlanogramRelaxConstraint[];
  metrics: {
    returnOnSpace: number | null;
    shelfAlignment: number | null;
    coreRange: number | null;
    dosMos: number | null;
  };

  containers: PlanogramContainer[];
  unrangedContainers: UnrangedPlanogramContainer[];
  planogram: Planogram;
  unrangedItems: UnrangedPlanogramItem[];
  deletedItems: (PlanogramItemCommonNonCircular & { storeCodes: string[] })[];

  constructor(pog: PogNonCircular) {
    this.version = pog.version;
    this.relaxedConstraints = pog.relaxedConstraints.map((relaxedConstraint) => ({
      ...relaxedConstraint,
    }));
    this.metrics = { ...pog.metrics };
    this.containers = pog.containers.map((container) => new PlanogramContainer(container, this));
    this.unrangedContainers = pog.unrangedContainers.map(
      (container) => new UnrangedPlanogramContainer(container, this),
    );
    this.planogram = new Planogram(pog.planogram, this);
    this.unrangedItems = pog.unrangedItems.map(
      (unrangedItem) => new UnrangedPlanogramItem(unrangedItem, this),
    );
    this.deletedItems = pog.deletedItems.map((item) => ({ ...item }));
  }

  addContainer(container: PlanogramContainer) {
    this.containers.push(container);
    return container;
  }

  getBay = (bayId: string) => {
    return this.planogram.getBay(bayId);
  };

  getShelf = (shelfId: string) => {
    return this.planogram.getShelf(shelfId);
  };

  getItem = (itemId: string) => {
    const planogramItem = this.planogram.getItem(itemId);
    const unrangedPlanogramItem = this.getUnrangedItem(itemId);
    return planogramItem || unrangedPlanogramItem;
  };

  getItems = (itemIds: string[]) => {
    const items = [];

    for (const itemId of itemIds) {
      items.push(this.getItem(itemId));
    }

    return items;
  };

  getUnrangedItem = (itemId: string) => {
    return this.unrangedItems.find((item) => item.uniqueId === itemId) || null;
  };

  calculateMetrics = () => {
    this.metrics = {
      returnOnSpace: this.calculateReturnOnSpace(),
      coreRange: this.calculateCoreRange(),
      shelfAlignment: this.calculateShelfAlignment(),
      dosMos: this.calculateDosMos(),
    };
  };

  getRecommendedQuantities = () => {
    const recommendedQuantities: { [key: string]: number | undefined } = {};

    for (const bay of this.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          const previousRecommendedQuantity = recommendedQuantities[item.variant];

          if (
            previousRecommendedQuantity === undefined ||
            item.quantity < previousRecommendedQuantity
          ) {
            recommendedQuantities[item.variant] = item.quantity;
          }
        }
      }
    }

    return recommendedQuantities;
  };

  isHavingRangeRecommendations = () => {
    const recommendedQuantities = this.getRecommendedQuantities();

    for (const unrangedItem of this.unrangedItems) {
      const recommendedQuantity = recommendedQuantities[unrangedItem.variant];
      if (recommendedQuantity !== undefined && unrangedItem.quantity > recommendedQuantity) {
        return true;
      }
    }

    return false;
  };

  calculateDosMos = () => {
    let totalItems = 0;
    let itemsBelowDosMos = 0;

    for (const bay of this.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          const dos = item.getDaysOfSupplyPercentage();
          const mos = item.getMOSPercentage();

          itemsBelowDosMos += dos < 100 || mos < 100 ? 1 : 0;
          totalItems++;
        }
      }
    }

    if (totalItems === 0 || isNaN(itemsBelowDosMos) || isNaN(totalItems)) {
      return null;
    }

    return (1 - itemsBelowDosMos / totalItems) * 100;
  };

  calculateShelfAlignment = () => {
    let totalBreakPoints = 0;
    let totalMisalignments = 0;
    const usedShelves: { [shelfId: string]: true } = {};

    for (let bayIndex = 0; bayIndex < this.planogram.bays.length; bayIndex++) {
      const bay = this.planogram.bays[bayIndex];
      const nextBay: PlanogramBay | undefined = this.planogram.bays[bayIndex + 1];
      if (!nextBay) break;

      shelfAlignmentChecker: for (const shelf of bay.shelves) {
        const shelfBottom = shelf.getYBottom();
        totalBreakPoints++;

        for (const nextBayShelf of nextBay.shelves) {
          // Shelf already used.
          if (usedShelves[nextBayShelf.uniqueId]) continue;

          // Shelf alignment found.
          if (nextBayShelf.getYBottom() === shelfBottom) {
            usedShelves[nextBayShelf.uniqueId] = true;
            continue shelfAlignmentChecker;
          }
        }

        // All next bay shelves checked, misalignment found.
        totalMisalignments++;
      }
    }

    if (totalBreakPoints === 0) {
      return null;
    }

    return (1 - totalMisalignments / totalBreakPoints) * 100;
  };

  calculateCoreRange = () => {
    const currentCoreRangeHash: Record<string, number> = {};
    const totalCoreRangeHash: Record<string, number> = {};

    this.planogram.bays.forEach((bay) => {
      bay.shelves.forEach((shelf) => {
        shelf.items.forEach((item) => {
          if (item.inCoreRange) {
            currentCoreRangeHash[item.productCode] = 1;
            totalCoreRangeHash[item.productCode] = 1;
          }
        });
      });
    });

    this.unrangedItems.forEach((unrangedItem) => {
      if (unrangedItem.inCoreRange) {
        totalCoreRangeHash[unrangedItem.productCode] = 1;
      }
    });

    const currentCoreRangeItems = Object.keys(currentCoreRangeHash).length;
    const totalCoreRangeItems = Object.keys(totalCoreRangeHash).length;

    if (
      totalCoreRangeItems === 0 ||
      Number.isNaN(currentCoreRangeItems) ||
      Number.isNaN(totalCoreRangeItems)
    ) {
      return null;
    }

    return (currentCoreRangeItems / totalCoreRangeItems) * 100;
  };

  calculateReturnOnSpace = () => {
    const sumTotal = {
      width: 0,
      salesAmount: 0,
    };

    const sumsByCDT1: {
      [cdt1: string]: {
        width: number;
        salesAmount: number;
      };
    } = {};

    for (const bay of this.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          if (!sumsByCDT1[item.cdt1]) {
            sumsByCDT1[item.cdt1] = {
              width: 0,
              salesAmount: 0,
            };
          }

          const itemSumWidthHorizontalFacings =
            item.getWidth() * Math.ceil(item.facings / item.facingsRows);

          sumsByCDT1[item.cdt1].width += itemSumWidthHorizontalFacings;
          sumsByCDT1[item.cdt1].salesAmount += item.salesAmount;

          sumTotal.width += itemSumWidthHorizontalFacings;
          sumTotal.salesAmount += item.salesAmount;
        }
      }
    }

    let penalty = 0;

    for (const sumByCDT1 of Object.values(sumsByCDT1)) {
      const widthPercent = (sumByCDT1.width / sumTotal.width) * 100;
      const salesAmountPercent = (sumByCDT1.salesAmount / sumTotal.salesAmount) * 100;

      if (widthPercent < salesAmountPercent) {
        penalty += Math.abs(widthPercent - salesAmountPercent);
      }
    }

    if (isNaN(penalty)) {
      return null;
    }

    return 100 - penalty;
  };

  toNonCircular = () => {
    const pogNonCircular: PogNonCircular = {
      version: this.version,
      planogram: {
        bays: this.planogram.bays.map((bay) => {
          const bayNonCircular: PlanogramBayNonCircular = {
            uniqueId: bay.uniqueId,
            width: bay.width,
            height: bay.height,
            depth: bay.depth,
            baseHeight: bay.baseHeight,
            notchOffset: bay.notchOffset,
            notchSpacing: bay.notchSpacing,
            maxNotch: bay.maxNotch,
            maxProductHead: bay.maxProductHead,
            maxShelfHeight: bay.maxShelfHeight,
            shelves: bay.shelves.map((shelf) => {
              const shelfNonCircular: PlanogramShelfNonCircular = {
                uniqueId: shelf.uniqueId,
                width: shelf.width,
                merchandisableWidth: shelf.merchandisableWidth,
                weightCapacity: shelf.weightCapacity,
                effectiveWeightLimit: shelf.effectiveWeightLimit,
                depth: shelf.depth,
                thickness: shelf.thickness,
                offset: shelf.offset,
                notchNo: shelf.getNotchNo(),
                grillHeight: shelf.grillHeight,
                type: shelf.type,
                variantRestrictions: shelf.variantRestrictions.map((variantRestriction) => ({
                  ...variantRestriction,
                })),
                bay: shelf.bay.uniqueId,
                mergedLeft: shelf.mergedLeft?.uniqueId || null,
                mergedRight: shelf.mergedRight?.uniqueId || null,
                items: shelf.items.map((item) => {
                  const itemNonCircular: PlanogramItemNonCircular = {
                    ...adapterPlanogramItemToPlanogramItemCommon(item),
                    shelf: item.shelf.uniqueId,
                  };

                  return itemNonCircular;
                }),
                align: shelf.align || "between",
              };

              return shelfNonCircular;
            }),

            pegboards: bay.pegboards.map((pegboard) => ({
              ...pegboard,
              bay: bay.uniqueId,
              placements: pegboard.placements.map((item) => ({
                ...item,
                pegboard: pegboard.uniqueId,
              })),
            })),
          };

          return bayNonCircular;
        }),
      },
      unrangedItems: this.unrangedItems.map((unrangedItem) => {
        const unrangedItemNonCircular: UnrangedPlanogramItemNonCircular = {
          ...adapterPlanogramItemToPlanogramItemCommon(unrangedItem),
          potentialShelves: [...unrangedItem.potentialShelves],
          reasons: [...unrangedItem.reasons],
          shelf: null,
        };

        return unrangedItemNonCircular;
      }),
      deletedItems: this.deletedItems.map((item) => ({ ...item, shelf: null })),
      containers: this.containers.map((container) => ({
        uniqueId: container.uniqueId,
        color: container.color,
        cdts: container.cdts.map((cdts) => ({ ...cdts })),
        xLeft: container.xLeft,
        xRight: container.xRight,
        yBottom: container.yBottom,
        yTop: container.yTop,
        shelfType: container.shelfType,
        sales: container.sales,
        minProductHeight: container.minProductHeight,
        maxProductHeight: container.maxProductHeight,
        algoCurrentTargetWidth: container.algoCurrentTargetWidth,
        algoMinTargetWidth: container.algoMinTargetWidth,
        algoTargetWidth: container.algoTargetWidth,
      })),
      unrangedContainers: this.unrangedContainers.map((unrangedContainer) => ({
        uniqueId: unrangedContainer.uniqueId,
        color: unrangedContainer.color,
        cdts: unrangedContainer.cdts.map((cdts) => ({ ...cdts })),
        xLeft: unrangedContainer.xLeft,
        xRight: unrangedContainer.xRight,
        yBottom: unrangedContainer.yBottom,
        yTop: unrangedContainer.yTop,
        shelfType: unrangedContainer.shelfType,
        sales: unrangedContainer.sales,
        minProductHeight: unrangedContainer.minProductHeight,
        maxProductHeight: unrangedContainer.maxProductHeight,
        algoCurrentTargetWidth: unrangedContainer.algoCurrentTargetWidth,
        algoMinTargetWidth: unrangedContainer.algoMinTargetWidth,
        algoTargetWidth: unrangedContainer.algoTargetWidth,
      })),
      relaxedConstraints: [...this.relaxedConstraints],
      metrics: { ...this.metrics },
    };

    return pogNonCircular;
  };
}

export type TemplatePogThumbnail =
  | {
      status: "IN_PROGRESS" | "ERROR" | "NOT_STARTED";
      url?: never;
    }
  | {
      status: "DONE";
      url: string;
    };

export interface PogData {
  id: number | null;
  pog_data?: PogNonCircular | null;
  is_favourite: boolean;
  solution_id: number;
}
